import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
  ArrowRight,
  Plus,
  Trash2,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Check
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminRedirects = () => {
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    from_path: '',
    to_path: '',
    is_permanent: true
  });
  const [saving, setSaving] = useState(false);
  
  const { toast } = useToast();
  const { token } = useAuth();

  useEffect(() => {
    fetchRedirects();
  }, []);

  const fetchRedirects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/redirects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRedirects(response.data);
    } catch (error) {
      console.error('Error fetching redirects:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke laste redirects",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.from_path || !formData.to_path) {
      toast({
        title: "Feil",
        description: "Begge stier er påkrevd",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API}/redirects`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Opprettet! ✅",
        description: `Redirect fra ${formData.from_path} opprettet`
      });
      
      setIsModalOpen(false);
      setFormData({ from_path: '', to_path: '', is_permanent: true });
      await fetchRedirects();
    } catch (error) {
      console.error('Error creating redirect:', error);
      toast({
        title: "Feil",
        description: error.response?.data?.detail || "Kunne ikke opprette redirect",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (redirectId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne redirecten?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/redirects/${redirectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Slettet! ✅",
        description: "Redirect fjernet"
      });
      
      await fetchRedirects();
    } catch (error) {
      console.error('Error deleting redirect:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke slette redirect",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white text-xl">Laster redirects...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">
            <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">
              301 Redirects
            </span>
          </h2>
          <p className="text-gray-400">Administrer URL-omdirigeringer for SEO og brukeropplevelse</p>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ny redirect
        </Button>
      </div>

      {/* Info Box */}
      <Card className="bg-blue-900/20 border-blue-600/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
            <div className="text-sm text-gray-300">
              <p className="font-semibold text-blue-400 mb-1">Hva er 301 redirects?</p>
              <p>En 301 redirect forteller søkemotorer at en side er permanent flyttet til en ny URL. Dette bevarer SEO-verdien og sikrer at brukere finner riktig innhold.</p>
              <p className="mt-2 text-gray-400">Eksempel: /gamle-tjenester → /tjenester</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Redirects List */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-red-400" />
            Aktive redirects ({redirects.length})
          </CardTitle>
          <CardDescription className="text-gray-400">
            Klikk på en redirect for å teste den
          </CardDescription>
        </CardHeader>
        <CardContent>
          {redirects.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <RefreshCw className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Ingen redirects ennå</p>
              <p className="text-sm mt-2">Klikk "Ny redirect" for å komme i gang</p>
            </div>
          ) : (
            <div className="space-y-2">
              {redirects.map((redirect) => (
                <div
                  key={redirect.id}
                  className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <code className="text-red-400 bg-red-900/20 px-2 py-1 rounded text-sm truncate max-w-[200px]">
                          {redirect.from_path}
                        </code>
                        <ArrowRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        <code className="text-green-400 bg-green-900/20 px-2 py-1 rounded text-sm truncate max-w-[200px]">
                          {redirect.to_path}
                        </code>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Badge 
                          className={redirect.is_permanent 
                            ? "bg-blue-600/20 text-blue-400 border-blue-600/50" 
                            : "bg-yellow-600/20 text-yellow-400 border-yellow-600/50"
                          }
                        >
                          {redirect.is_permanent ? '301' : '302'}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {redirect.hit_count} treff
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-600 text-gray-300"
                        onClick={() => window.open(redirect.from_path, '_blank')}
                        title="Test redirect"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-600 text-red-400 hover:bg-red-600/20"
                        onClick={() => handleDelete(redirect.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Opprett ny redirect</DialogTitle>
            <DialogDescription className="text-gray-400">
              Omdiriger en gammel URL til en ny
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300 mb-2 block">Fra (gammel URL) *</Label>
              <Input
                value={formData.from_path}
                onChange={(e) => setFormData({ ...formData, from_path: e.target.value })}
                placeholder="/gammel-side"
                className="bg-gray-800 border-gray-700 text-white"
              />
              <p className="text-xs text-gray-500 mt-1">Sti uten domene, f.eks. /gammel-side</p>
            </div>
            
            <div>
              <Label className="text-gray-300 mb-2 block">Til (ny URL) *</Label>
              <Input
                value={formData.to_path}
                onChange={(e) => setFormData({ ...formData, to_path: e.target.value })}
                placeholder="/ny-side"
                className="bg-gray-800 border-gray-700 text-white"
              />
              <p className="text-xs text-gray-500 mt-1">Kan være intern sti eller full URL</p>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_permanent"
                checked={formData.is_permanent}
                onChange={(e) => setFormData({ ...formData, is_permanent: e.target.checked })}
                className="w-4 h-4 rounded border-gray-700 bg-gray-900"
              />
              <Label htmlFor="is_permanent" className="text-gray-300 cursor-pointer">
                Permanent redirect (301) - anbefalt for SEO
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              className="border-gray-600 text-gray-300"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleCreate}
              disabled={saving}
              className="bg-gradient-to-r from-red-600 to-yellow-500"
            >
              {saving ? 'Oppretter...' : 'Opprett redirect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRedirects;
